import type { AdministrationRole } from './permissions.js';

/**
 * One navigation entry of the application frame, described as data rather
 * than as markup.
 *
 * Every navigation surface of the frame (the account popover, the work
 * areas of a fertilizer year) declares its entries as a list of these and
 * renders that list, so an added entry needs neither a state field nor a
 * route check of its own.
 */
export interface INavigationEntry {

    /**
     * Stable English identifier. Serves as the render key and as the hook
     * for the few entries that carry extra chrome next to their label; it
     * is never shown to the user.
     */
    id: string;

    /** The label the user reads. */
    label: string;

    /** The path the entry navigates to. */
    path: string;

    /**
     * Administration atom this entry is bound to. An entry naming one is
     * offered only while the caller holds it. Entries below a farm context
     * carry none: reaching that context already required read access to the
     * farm, so a second gate would only restate it.
     */
    permission?: AdministrationRole;
}

/**
 * Whether the entry describes the current location.
 *
 * The match is segment-scoped, so an entry stays marked while a child route
 * of its path is open (`/account/email/edit` keeps "Mein Konto" marked)
 * without a path that merely shares its prefix (`/accounts`) matching it.
 * @param entry - The entry to test.
 * @param pathname - The current pathname.
 * @returns Whether the entry is the active one.
 */
export function isNavigationEntryActive (entry: INavigationEntry, pathname: string): boolean {
    return pathname === entry.path || pathname.startsWith(`${entry.path}/`);
}

/**
 * Whether the caller may be offered the entry. Fails closed with the
 * permissions snapshot it is fed: an unresolved snapshot yields no held
 * atoms, which hides the bound entries rather than surfacing entries whose
 * routes the server would reject.
 * @param entry - The entry to test.
 * @param heldPermissions - The administration atoms the caller holds.
 * @returns Whether the entry is visible.
 */
export function isNavigationEntryVisible (
    entry: INavigationEntry,
    heldPermissions: ReadonlyArray<AdministrationRole>
): boolean {
    return entry.permission === undefined || heldPermissions.includes(entry.permission);
}
